import React from "react";
import { Box, Divider, IconButton, Typography, useTheme } from "@mui/material";
import { TotalOrdersPanelProps } from "./TotalOrdersPanel.types";
import { useTotalOrdersPanel } from "./TotalOrdersPanel.hooks";
import { ComponentLoader, InfoTooltip, Panel } from "../../components";
import { Decrease, Increase, InfoOutline } from "@tiny/icons";
import DonutChart from "../../components/DonutChart/DonutChart";
import { useConfigView } from "../../Config.context";
import { formatNumber } from "@tiny/utils/formatUtils";

function TotalOrdersPanel(props: TotalOrdersPanelProps) {
  const { statistics, isLoadingStatistics, lastPeriod } =
    useTotalOrdersPanel(props);

  const theme = useTheme();
  const { config } = useConfigView();
  const colors =
    config?.appName === "tinyEmail"
      ? [theme.palette.primary[40] || "", theme.palette.primary[100] || ""]
      : [theme.palette.yellow[100] || "", theme.palette.primary[100] || ""];

  return (
    <Panel
      title="Total Orders"
      showDivider
      actions={
        <InfoTooltip
          message="Total orders generated from campaigns and workflows"
          variant="white"
          noInfoIcon
        >
          <IconButton sx={{ p: 0, ml: 1 }}>
            <InfoOutline />
          </IconButton>
        </InfoTooltip>
      }
    >
      {isLoadingStatistics ? (
        <ComponentLoader height="270px" />
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "center", md: "stretch" },
            px: 5,
            gap: 5,
          }}
        >
          <Box
            sx={{
              flex: "0 0 40%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "240px",
              position: "relative",
            }}
          >
            <DonutChart
              data={[
                {
                  label: "Campaigns",
                  value: statistics?.campaignOrders?.currentOrders || 0,
                },
                {
                  label: "Workflows",
                  value: statistics?.workflowOrders?.currentOrders || 0,
                },
              ]}
              colors={colors}
            />
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                textAlign: "center",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              <Typography variant="h1" fontWeight="bold" color="grey.100">
                {formatNumber(statistics?.totalOrders?.currentOrders || 0)}
              </Typography>
              <PercentageIndicator
                comparePercent={statistics?.totalOrders?.comparePercent}
                lastPeriod={lastPeriod}
              />
            </Box>
          </Box>
          <Box
            sx={{
              flex: "1",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: 2,
              width: "100%",
            }}
          >
            <MetricInfoCard
              image={
                "https://tinyemail-stripo.s3.ca-central-1.amazonaws.com/static/dashboard/campaigns_revenue.svg"
              }
              label={"Campaigns"}
              value={formatNumber(
                statistics?.campaignOrders?.currentOrders || 0
              )}
              comparePercent={statistics?.campaignOrders?.comparePercent || 0}
              lastPeriod={lastPeriod}
            />
            <Divider sx={{ width: "100%" }} />
            <MetricInfoCard
              image={
                "https://tinyemail-stripo.s3.ca-central-1.amazonaws.com/static/dashboard/workflows_revenue.svg"
              }
              label={"Workflows"}
              value={formatNumber(
                statistics?.workflowOrders?.currentOrders || 0
              )}
              comparePercent={statistics?.workflowOrders?.comparePercent || 0}
              lastPeriod={lastPeriod}
            />
          </Box>
        </Box>
      )}
    </Panel>
  );
}

export default TotalOrdersPanel;

function PercentageIndicator({
  comparePercent,
  lastPeriod,
}: {
  comparePercent: number | null | undefined;
  lastPeriod: string;
}) {
  const theme = useTheme();

  if (
    comparePercent === null ||
    comparePercent === undefined ||
    comparePercent === 0
  ) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mt: 0.5,
        }}
      >
        <Typography variant="caption" color="grey.80">
          0.0% vs last {lastPeriod}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mt: 0.5,
      }}
    >
      {comparePercent >= 0 ? (
        <>
          <Increase
            sx={{
              color: theme.palette.green.dark,
              fontSize: "10px !important",
              mr: 0.5,
            }}
          />
          <Typography variant="caption" color="green.dark">
            {Math.abs(comparePercent).toFixed(1)}%
          </Typography>
        </>
      ) : (
        <>
          <Decrease
            sx={{
              color: theme.palette.red.main,
              fontSize: "10px !important",
              mr: 0.5,
            }}
          />
          <Typography variant="caption" color="red.main">
            {Math.abs(comparePercent).toFixed(1)}%
          </Typography>
        </>
      )}
      <Typography variant="caption" color="grey.80">
        &nbsp;vs last {lastPeriod}
      </Typography>
    </Box>
  );
}

function MetricInfoCard({
  image,
  label,
  value,
  comparePercent,
  lastPeriod,
}: {
  image: string;
  label: string;
  value: string | number;
  comparePercent?: number | null;
  lastPeriod: string;
}) {
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      width="100%"
      alignItems="flex-start"
      py={2}
    >
      <Box
        overflow="hidden"
        display="flex"
        gap={2}
        flexDirection="column"
        flex={1}
        alignItems={"flex-start"}
      >
        <Typography variant="label1R" color="grey.80">
          {label}
        </Typography>
        <Typography variant="h1" color="grey.90">
          {typeof value === "number" ? value.toLocaleString() : value}
        </Typography>
        {comparePercent !== undefined && (
          <PercentageIndicator
            comparePercent={comparePercent}
            lastPeriod={lastPeriod}
          />
        )}
      </Box>
      <Box sx={{ maxWidth: "80px", flexShrink: 0, ml: 2 }}>
        {image && (
          <img
            src={image}
            alt={label}
            style={{
              width: "100%",
              height: "auto",
              maxHeight: "60px",
              objectFit: "contain",
            }}
          />
        )}
      </Box>
    </Box>
  );
}
